import { useCallback, useContext, useMemo, useState } from 'react';
import {
  openTelegramBiometricSettings,
  requestTelegramBiometricInfo,
  requestTelegramBiometricAccess,
  updateTelegramBiometricToken,
  useTelegram,
} from './useTelegram';
import { authApi } from '../api/services/authApi';
import { CurrentUserContext } from '../contexts/currentUserContext';
import { TranslationContext } from '../contexts/translationContext';
import {
  generateBiometricKeyPair,
  publicJwkToSpkiPem,
  signBiometricPayload,
} from '../services/biometricCrypto';

const BIOMETRIC_PERMISSION_CHECK_TIMEOUT_MS = 5000;
const BIOMETRIC_TOKEN_RECOVERY_TIMEOUT_MS = 30000;
let biometricTokenRecoveryPromise = null;
let biometricStartEnrollmentPromise = null;
let biometricDisableEnrollmentPromise = null;

export const BIOMETRIC_ENROLLMENT_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  REQUESTING_ACCESS: 'requesting_access',
  OPENING_SETTINGS: 'opening_settings',
  CHECK_PERMISSION: 'check_permission',
  TOKEN_NEEDED: 'token_needed',
  SETTINGS_NEEDED: 'settings_needed',
  GENERATING_KEYS: 'generating_keys',
  SAVING_TOKEN: 'saving_token',
  SYNCING: 'syncing',
  DISABLING: 'disabling',
  DISABLED: 'disabled',
  SUCCESS: 'success',
  DENIED: 'denied',
  ERROR: 'error',
};

function getBiometricLabelKey(biometricType) {
  if (biometricType === 'face') {
    return 'profile.biometric.labels.face';
  }

  if (biometricType === 'finger') {
    return 'profile.biometric.labels.finger';
  }

  return 'profile.biometric.labels.unknown';
}

function getUserBiometryDevicePresent(user) {
  const value = user?.biometry_device_present ?? user?.biometryDevicePresent;

  return typeof value === 'boolean' ? value : null;
}

function getAvailabilityFromBiometric(biometric, user) {
  const isAvailable = biometric?.isBiometricAvailable === true;
  const isAccessRequested = biometric?.isAccessRequested === true;
  const isAccessGranted = biometric?.isAccessGranted === true;
  const isTokenSaved = biometric?.isBiometricTokenSaved === true;
  const backendDevicePresent = getUserBiometryDevicePresent(user);
  const isBackendDevicePresent = backendDevicePresent !== false;
  const isEnabled = isAvailable && isAccessGranted && isTokenSaved && isBackendDevicePresent;

  return {
    isAvailable,
    isAccessRequested,
    isAccessGranted,
    isEnabled,
    shouldPromoteAccess: isAvailable && !isAccessRequested,
    shouldOpenSettings: isAvailable && isAccessRequested && !isAccessGranted,
    isReadyForToken: isAvailable && isAccessGranted,
    isTokenSaved,
    isBackendDevicePresent,
    shouldFinishSetup: isAvailable
      && isAccessGranted
      && (!isTokenSaved || !isBackendDevicePresent),
  };
}

function userHasLoginToken(user) {
  return Boolean(
    user?.token
    || user?.forgot_password_token
    || user?.forgotPasswordToken
    || user?.login_token
    || user?.loginToken
    || user?.auth_token
    || user?.authToken
  );
}

function getCurrentUserBiometricReadiness(user) {
  const stages = user?.reg_data?.stages || {};
  const signedDocuments = user?.signed_legal_documents;
  const signedLegalDocumentsCount = Array.isArray(signedDocuments)
    ? signedDocuments.length
    : null;
  const checks = {
    hasUser: Boolean(user),
    hasUserId: Boolean(user?._id || user?.id || user?.user_id),
    hasLoginToken: userHasLoginToken(user),
    isSystemAllowed: stages.is_system_allowed !== false,
    isPhoneVerified: stages.is_phone_verified === true,
    isPasswordSet: stages.is_password_set === true,
    hasSignedLegalDocuments: signedLegalDocumentsCount !== null
      && signedLegalDocumentsCount > 0,
  };
  const failedChecks = Object.entries(checks)
    .filter(([key, value]) => (
      key === 'hasLoginToken' ? value === true : value !== true
    ))
    .map(([key]) => key);

  return {
    isReady: failedChecks.length === 0,
    failedChecks,
    userId: user?._id || user?.id || user?.user_id || null,
    signedLegalDocumentsCount,
    stages: {
      is_system_allowed: stages.is_system_allowed,
      is_phone_verified: stages.is_phone_verified,
      is_password_set: stages.is_password_set,
    },
  };
}

function formatBiometricDebug(biometric) {
  return [
    `available=${biometric?.isBiometricAvailable === true ? '1' : '0'}`,
    `requested=${biometric?.isAccessRequested === true ? '1' : '0'}`,
    `granted=${biometric?.isAccessGranted === true ? '1' : '0'}`,
    `token=${biometric?.isBiometricTokenSaved === true ? '1' : '0'}`,
    `type=${biometric?.biometricType || 'none'}`,
    `device=${biometric?.deviceId ? '1' : '0'}`,
  ].join(' ');
}

function showBiometricDebug() {
  return undefined;
}

function getCurrentUserId(currentUser) {
  return currentUser?._id
    ?? currentUser?.id
    ?? currentUser?.user_id
    ?? '';
}

async function buildAddBiometryPayload({ currentUser, deviceId, privateJwk, publicJwk }) {
  const userId = String(getCurrentUserId(currentUser));
  const normalizedDeviceId = String(deviceId || '');

  if (!userId) {
    throw new Error('Missing current user id.');
  }

  if (!normalizedDeviceId) {
    throw new Error('Missing biometric device id.');
  }

  const timestamp = Math.floor(Date.now() / 1000);
  const payload = `add ${userId} ${normalizedDeviceId} ${timestamp}`;
  const { signature } = await signBiometricPayload({
    privateKeyToken: privateJwk,
    payload,
  });

  return {
    public_key: publicJwkToSpkiPem(publicJwk),
    signature,
    device_id: normalizedDeviceId,
    timestamp,
  };
}

export function useBiometricEnrollment() {
  const { biometric, webApp } = useTelegram();
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const { t } = useContext(TranslationContext);
  const [status, setStatus] = useState(BIOMETRIC_ENROLLMENT_STATUS.IDLE);
  const [error, setError] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const biometricType = biometric?.biometricType || 'unknown';
  const biometricLabel = t(getBiometricLabelKey(biometricType));
  const enableButtonLabel = biometricType === 'unknown'
    ? t('profile.biometric.enableGeneric')
    : t('profile.biometric.enableSpecific', { label: biometricLabel });
  const isBusy = [
    BIOMETRIC_ENROLLMENT_STATUS.CHECKING,
    BIOMETRIC_ENROLLMENT_STATUS.REQUESTING_ACCESS,
    BIOMETRIC_ENROLLMENT_STATUS.OPENING_SETTINGS,
    BIOMETRIC_ENROLLMENT_STATUS.GENERATING_KEYS,
    BIOMETRIC_ENROLLMENT_STATUS.SAVING_TOKEN,
    BIOMETRIC_ENROLLMENT_STATUS.SYNCING,
    BIOMETRIC_ENROLLMENT_STATUS.DISABLING,
  ].includes(status);

  const availability = useMemo(
    () => getAvailabilityFromBiometric(biometric, currentUser),
    [biometric, currentUser]
  );
  const currentUserReadiness = useMemo(
    () => getCurrentUserBiometricReadiness(currentUser),
    [currentUser]
  );
  const canIssueBiometricToken = useMemo(
    () => currentUserReadiness.isReady,
    [currentUserReadiness.isReady]
  );
  const biometricAccessReason = useMemo(
    () => t('profile.biometric.accessReason'),
    [t]
  );

  const issueBiometricToken = useCallback(() => {
    showBiometricDebug(`issue token existing=${biometricTokenRecoveryPromise ? '1' : '0'}`);

    if (biometricTokenRecoveryPromise) {
      return biometricTokenRecoveryPromise;
    }

    let resolveIssue;
    let rejectIssue;
    let isSettled = false;
    let timeoutId = null;
    const nextIssuePromise = new Promise((resolve, reject) => {
      resolveIssue = resolve;
      rejectIssue = reject;
    });

    biometricTokenRecoveryPromise = nextIssuePromise;

    const cleanupIssuePromise = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (biometricTokenRecoveryPromise === nextIssuePromise) {
        biometricTokenRecoveryPromise = null;
      }
    };

    const settleSuccess = (result) => {
      if (isSettled) return;

      isSettled = true;
      cleanupIssuePromise();
      resolveIssue(result);
    };

    const settleError = (nextError) => {
      if (isSettled) return;

      isSettled = true;
      cleanupIssuePromise();
      rejectIssue(nextError);
    };

    timeoutId = window.setTimeout(() => {
      showBiometricDebug('issue token timeout');
      settleError(new Error('Biometric setup timed out. Check permission and try again.'));
    }, BIOMETRIC_TOKEN_RECOVERY_TIMEOUT_MS);

    (async () => {
      showBiometricDebug(`issue token start canIssue=${canIssueBiometricToken ? '1' : '0'}`);
      showBiometricDebug('issue token user readiness', currentUserReadiness);

      if (!canIssueBiometricToken) {
        showBiometricDebug('blocked: current user is not fully signed in or has login token');
        throw new Error('Biometric token can be issued only after full sign-in.');
      }

      const latestBiometric = await requestTelegramBiometricInfo(webApp);
      showBiometricDebug(`issue token get_info ${formatBiometricDebug(latestBiometric)}`);

      if (latestBiometric?.isAccessGranted !== true) {
        showBiometricDebug('blocked: access_granted is false before token update');
        throw new Error('Biometric access is not granted.');
      }

      setStatus(BIOMETRIC_ENROLLMENT_STATUS.GENERATING_KEYS);
      const { privateJwk, publicJwk, provider } = await generateBiometricKeyPair();
      showBiometricDebug(`keys generated provider=${provider || 'unknown'}`);

      setStatus(BIOMETRIC_ENROLLMENT_STATUS.SYNCING);
      const addBiometryPayload = await buildAddBiometryPayload({
        currentUser,
        deviceId: latestBiometric?.deviceId,
        privateJwk,
        publicJwk,
      });
      await authApi.addBiometry(addBiometryPayload);
      setCurrentUser((prevUser) => (
        prevUser
          ? { ...prevUser, biometry_device_present: true }
          : prevUser
      ));
      showBiometricDebug('public key synced');

      setStatus(BIOMETRIC_ENROLLMENT_STATUS.SAVING_TOKEN);
      showBiometricDebug('update_token start');
      const tokenResult = await updateTelegramBiometricToken(webApp, JSON.stringify(privateJwk));
      showBiometricDebug(
        `update_token result status=${tokenResult?.status || 'none'} updated=${tokenResult?.isUpdated ? '1' : '0'} posted=${tokenResult?.isPosted === false ? '0' : '1'}`
      );

      if (!tokenResult?.isUpdated) {
        throw new Error('Failed to save biometric token.');
      }

      const finalBiometric = await requestTelegramBiometricInfo(webApp);
      showBiometricDebug(`final get_info ${formatBiometricDebug(finalBiometric)}`);
      setStatus(BIOMETRIC_ENROLLMENT_STATUS.SUCCESS);
      showBiometricDebug('setup success');

      return {
        success: true,
        publicJwk,
      };
    })().then(settleSuccess, settleError);

    return nextIssuePromise;
  }, [canIssueBiometricToken, currentUser, currentUserReadiness, setCurrentUser, webApp]);

  const startEnrollment = useCallback(({
    checkPermission = false,
    intent = 'setup',
    useSettings = false,
  } = {}) => {
    if (biometricStartEnrollmentPromise) {
      return biometricStartEnrollmentPromise;
    }

    let resolveStart;
    let rejectStart;
    const nextStartEnrollmentPromise = new Promise((resolve, reject) => {
      resolveStart = resolve;
      rejectStart = reject;
    });

    biometricStartEnrollmentPromise = nextStartEnrollmentPromise;
    setActiveAction(intent);

    (async () => {
      if (isBusy) {
        return {
          success: false,
          reason: 'busy',
        };
      }

      setError(null);

      try {
        showBiometricDebug(
          `start check=${checkPermission ? '1' : '0'} settings=${useSettings ? '1' : '0'} status=${status}`
        );
        showBiometricDebug('start user readiness', currentUserReadiness);
        setStatus(BIOMETRIC_ENROLLMENT_STATUS.CHECKING);
        const latestBiometric = await requestTelegramBiometricInfo(webApp, {
          timeoutMs: checkPermission
            ? BIOMETRIC_PERMISSION_CHECK_TIMEOUT_MS
            : undefined,
        });
        showBiometricDebug(`start get_info ${formatBiometricDebug(latestBiometric)}`);
        const latestAvailability = getAvailabilityFromBiometric(latestBiometric, currentUser);
        const effectiveAvailability = latestAvailability.isAvailable
          ? latestAvailability
          : availability;
        showBiometricDebug(
          `availability saved=${effectiveAvailability.isTokenSaved ? '1' : '0'} finish=${effectiveAvailability.shouldFinishSetup ? '1' : '0'} openSettings=${effectiveAvailability.shouldOpenSettings ? '1' : '0'} canIssue=${canIssueBiometricToken ? '1' : '0'}`
        );

        if (!effectiveAvailability.isAvailable) {
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.IDLE);
          showBiometricDebug('stop: biometrics unavailable');

          return {
            success: false,
            reason: 'unavailable',
          };
        }

        if (effectiveAvailability.isEnabled) {
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.SUCCESS);
          showBiometricDebug('already enabled: access_granted and token_saved true');

          return {
            success: true,
            alreadyEnabled: true,
          };
        }

        let hasAccess = effectiveAvailability.isAccessGranted;
        const shouldOpenSettings = useSettings || effectiveAvailability.shouldOpenSettings;

        if (hasAccess && effectiveAvailability.shouldFinishSetup) {
          if (intent !== 'setup') {
            setActiveAction('finish_setup');
          }
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.TOKEN_NEEDED);
          showBiometricDebug('branch: access granted, token missing');
          return await issueBiometricToken();
        }

        if (checkPermission && !hasAccess) {
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.SETTINGS_NEEDED);
          showBiometricDebug('branch: check permission failed, open settings again');

          return {
            success: false,
            reason: 'settings_needed',
          };
        }

        if (shouldOpenSettings && !hasAccess) {
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.OPENING_SETTINGS);
          showBiometricDebug('branch: open Telegram biometric settings');
          openTelegramBiometricSettings(webApp);
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.CHECK_PERMISSION);

          return {
            success: false,
            reason: 'settings_opened',
          };
        }

        if (!hasAccess) {
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.REQUESTING_ACCESS);
          showBiometricDebug('request_access start');
          const accessResult = await requestTelegramBiometricAccess(webApp, {
            reason: biometricAccessReason,
          });
          hasAccess = accessResult?.accessGranted === true;
          showBiometricDebug('request_access raw result', accessResult);
          showBiometricDebug(`request_access result granted=${hasAccess ? '1' : '0'}`);
        }

        if (!hasAccess) {
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.DENIED);
          showBiometricDebug('stop: access denied');

          return {
            success: false,
            reason: 'denied',
          };
        }

        if (!canIssueBiometricToken) {
          setStatus(BIOMETRIC_ENROLLMENT_STATUS.IDLE);
          showBiometricDebug('stop after access: current user cannot issue biometric token');

          return {
            success: false,
            reason: 'not_signed_in',
          };
        }

        return await issueBiometricToken();
      } catch (nextError) {
        setError(nextError);
        setStatus(BIOMETRIC_ENROLLMENT_STATUS.ERROR);
        showBiometricDebug(`error: ${nextError?.message || 'unknown'}`);

        return {
          success: false,
          reason: 'error',
          error: nextError,
        };
      }
    })().then(resolveStart, rejectStart);

    nextStartEnrollmentPromise.finally(() => {
      if (biometricStartEnrollmentPromise === nextStartEnrollmentPromise) {
        biometricStartEnrollmentPromise = null;
      }
    });

    return nextStartEnrollmentPromise;
  }, [
    availability,
    biometricAccessReason,
    canIssueBiometricToken,
    currentUser,
    currentUserReadiness,
    issueBiometricToken,
    isBusy,
    status,
    webApp,
  ]);

  const resetEnrollment = useCallback(() => {
    biometricTokenRecoveryPromise = null;
    biometricStartEnrollmentPromise = null;
    biometricDisableEnrollmentPromise = null;
    setActiveAction(null);
    setStatus(BIOMETRIC_ENROLLMENT_STATUS.IDLE);
    setError(null);
  }, []);

  const refreshBiometricInfo = useCallback(async () => {
    try {
      const latestBiometric = await requestTelegramBiometricInfo(webApp);

      return latestBiometric;
    } catch (nextError) {
      return null;
    }
  }, [webApp]);

  const disableEnrollment = useCallback(() => {
    if (biometricDisableEnrollmentPromise) {
      return biometricDisableEnrollmentPromise;
    }

    let resolveDisable;
    let rejectDisable;
    const nextDisableEnrollmentPromise = new Promise((resolve, reject) => {
      resolveDisable = resolve;
      rejectDisable = reject;
    });

    biometricDisableEnrollmentPromise = nextDisableEnrollmentPromise;
    setActiveAction('disable');

    (async () => {
      if (isBusy) {
        return {
          success: false,
          reason: 'busy',
        };
      }

      setError(null);

      try {
        setStatus(BIOMETRIC_ENROLLMENT_STATUS.DISABLING);
        const tokenResult = await updateTelegramBiometricToken(webApp, '');

        if (tokenResult?.status !== 'removed') {
          throw new Error('Failed to disable biometric sign-in.');
        }

        await authApi.removeBiometry();
        setCurrentUser((prevUser) => (
          prevUser
            ? { ...prevUser, biometry_device_present: false }
            : prevUser
        ));

        biometricTokenRecoveryPromise = null;
        setStatus(BIOMETRIC_ENROLLMENT_STATUS.DISABLED);

        return {
          success: true,
        };
      } catch (nextError) {
        setError(nextError);
        setStatus(BIOMETRIC_ENROLLMENT_STATUS.ERROR);

        return {
          success: false,
          reason: 'error',
          error: nextError,
        };
      }
    })().then(resolveDisable, rejectDisable);

    nextDisableEnrollmentPromise.finally(() => {
      if (biometricDisableEnrollmentPromise === nextDisableEnrollmentPromise) {
        biometricDisableEnrollmentPromise = null;
      }
    });

    return nextDisableEnrollmentPromise;
  }, [isBusy, setCurrentUser, webApp]);

  return {
    activeAction,
    availability,
    biometric,
    biometricLabel,
    disableEnrollment,
    enableButtonLabel,
    error,
    isBusy,
    refreshBiometricInfo,
    resetEnrollment,
    startEnrollment,
    status,
  };
}
